import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Wrench, ClipboardCheck, CheckCircle2, Lightbulb, Plus, Save, RefreshCw, Shield, X, ClipboardList, Link2, Sparkles } from "lucide-react";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "@/hooks/use-toast";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { usePhaseRoles } from "@/hooks/usePhaseRoles";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  pointerWithin,
  rectIntersection,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { DraggableWorkflowItem } from "@/components/workflow/DraggableWorkflowItem";
import { PhaseMinutesInput } from "@/components/workflow/PhaseMinutesInput";
import { ItemMappingDialog } from "@/components/workflow/ItemMappingDialog";

const APPLIANCE_TYPES = ["Washing Machine", "Dryer", "Washer Dryer", "Dishwasher", "Oven", "Household Refrigerator"];

const CATEGORY_CONFIG = [
  { key: "refurbish_start", label: "Refurbish Start", icon: RefreshCw, brandSpecific: false },
  { key: "error_codes", label: "Common Error Codes", icon: AlertTriangle, brandSpecific: true },
  { key: "troubleshooting", label: "Pre-repair Troubleshooting", icon: ClipboardCheck, brandSpecific: false },
  { key: "parts_replacement", label: "Parts Replacement Guides", icon: Wrench, brandSpecific: false },
  { key: "verification", label: "Final Verification", icon: CheckCircle2, brandSpecific: false },
  { key: "cleaning", label: "Cleaning", icon: Sparkles, brandSpecific: false },
  { key: "tips", label: "Master Reset & Tips", icon: Lightbulb, brandSpecific: "mixed" as any },
] as const;

type WorkflowItem = {
  id: string;
  category: string;
  appliance_type: string;
  brand: string | null;
  title: string;
  description: string | null;
  sort_order: number;
  is_mandatory: boolean;
  estimated_minutes?: number | null;
};

type WorkflowDefault = {
  id: string;
  workflow_item_id: string;
  for_repair: boolean;
  for_refurbish: boolean;
  brand: string | null;
  estimated_minutes?: number | null;
};

function DroppableCategory({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`transition-colors rounded-lg ${isOver ? "ring-2 ring-primary/50 bg-primary/5" : ""}`}
    >
      {children}
    </div>
  );
}

export default function WorkflowManagement() {
  const { t } = useTranslation("admin");
  const { isSuperadmin: isAdmin, isLoading: roleLoading } = useIsSuperadmin();
  const navigate = useNavigate();
  const [applianceType, setApplianceType] = useState(APPLIANCE_TYPES[0]);
  const [selectedBrand, setSelectedBrand] = useState<string>("__all__");
  const [items, setItems] = useState<WorkflowItem[]>([]);
  const [defaults, setDefaults] = useState<WorkflowDefault[]>([]);
  const [parentDefaults, setParentDefaults] = useState<WorkflowDefault[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    error_codes: true, parts_replacement: true, troubleshooting: true, refurbish_start: true, verification: true, cleaning: true, tips: true,
  });
  const [newItemCategory, setNewItemCategory] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [customRoles, setCustomRoles] = useState<{ id: string; name: string }[]>([]);
  const { getRolesForPhase, addRoleToPhase, removeRoleFromPhase } = usePhaseRoles(applianceType);
  const [mappings, setMappings] = useState<{ id: string; source_item_id: string; target_item_id: string }[]>([]);
  const [mappingDialogItem, setMappingDialogItem] = useState<WorkflowItem | null>(null);

  // Phase-level enable/disable for repair vs refurbish
  type PhaseDefault = { id: string; phase_category: string; brand: string | null; for_repair: boolean; for_refurbish: boolean; estimated_minutes: number | null };
  const [phaseDefaults, setPhaseDefaults] = useState<PhaseDefault[]>([]);
  const [parentPhaseDefaults, setParentPhaseDefaults] = useState<PhaseDefault[]>([]);

  // Fetch custom roles
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("custom_roles").select("id, name");
      if (data) setCustomRoles(data);
    })();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    // Ensure auth token is fresh before querying RLS-protected tables.
    // If the session expired silently, supabase-js may otherwise send the
    // anon key only, which RLS rejects and returns an empty array.
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      await supabase.auth.refreshSession();
    }

    const { data: itemsData, error: itemsError } = await supabase
      .from("workflow_items")
      .select("*")
      .eq("appliance_type", applianceType)
      .order("sort_order", { ascending: true });

    if (itemsError) {
      toast({ title: "Failed to load workflow items", description: itemsError.message, variant: "destructive" });
    }

    const fetchedItems = (itemsData || []) as WorkflowItem[];
    setItems(fetchedItems);

    const uniqueBrands = [...new Set(fetchedItems.filter(i => i.category === "error_codes" && i.brand).map(i => i.brand!))];
    setBrands(uniqueBrands);

    // Fetch parent (brand-agnostic) defaults
    const { data: parentData } = await supabase
      .from("workflow_defaults")
      .select("*")
      .eq("appliance_type", applianceType)
      .is("brand", null);
    setParentDefaults((parentData || []) as WorkflowDefault[]);

    // Fetch brand-specific defaults
    const isAllBrands = selectedBrand === "__all__";
    if (!isAllBrands && selectedBrand) {
      const { data: defaultsData } = await supabase
        .from("workflow_defaults")
        .select("*")
        .eq("appliance_type", applianceType)
        .eq("brand", selectedBrand);
      setDefaults((defaultsData || []) as WorkflowDefault[]);
    } else {
      setDefaults([]);
    }
    // Fetch mappings for error code items
    const errorCodeIds = fetchedItems.filter(i => i.category === "error_codes").map(i => i.id);
    if (errorCodeIds.length > 0) {
      const { data: mappingsData } = await supabase
        .from("workflow_item_mappings")
        .select("id, source_item_id, target_item_id")
        .in("source_item_id", errorCodeIds);
      setMappings((mappingsData || []) as { id: string; source_item_id: string; target_item_id: string }[]);
    } else {
      setMappings([]);
    }

    // Fetch phase-level defaults (parent + brand-specific)
    const { data: parentPhaseData } = await supabase
      .from("workflow_phase_defaults")
      .select("id, phase_category, brand, for_repair, for_refurbish, estimated_minutes")
      .eq("appliance_type", applianceType)
      .is("brand", null);
    setParentPhaseDefaults((parentPhaseData || []) as PhaseDefault[]);

    if (!isAllBrands && selectedBrand) {
      const { data: brandPhaseData } = await supabase
        .from("workflow_phase_defaults")
        .select("id, phase_category, brand, for_repair, for_refurbish, estimated_minutes")
        .eq("appliance_type", applianceType)
        .eq("brand", selectedBrand);
      setPhaseDefaults((brandPhaseData || []) as PhaseDefault[]);
    } else {
      setPhaseDefaults([]);
    }

    setLoading(false);
  }, [applianceType, selectedBrand]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const isAllBrands = selectedBrand === "__all__";
    if (isAllBrands || !selectedBrand) {
      setDefaults([]);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("workflow_defaults")
        .select("*")
        .eq("appliance_type", applianceType)
        .eq("brand", selectedBrand);
      setDefaults((data || []) as WorkflowDefault[]);
    })();
  }, [selectedBrand, applianceType]);

  const isAllBrands = selectedBrand === "__all__";

  const isChecked = (itemId: string, type: "repair" | "refurbish") => {
    if (isAllBrands) {
      // Show parent defaults
      const d = parentDefaults.find(d => d.workflow_item_id === itemId);
      return d ? (type === "repair" ? d.for_repair : d.for_refurbish) : false;
    }
    // Brand-specific override takes priority
    const brandDefault = defaults.find(d => d.workflow_item_id === itemId);
    if (brandDefault) {
      return type === "repair" ? brandDefault.for_repair : brandDefault.for_refurbish;
    }
    // Fall back to parent
    const parentDefault = parentDefaults.find(d => d.workflow_item_id === itemId);
    return parentDefault ? (type === "repair" ? parentDefault.for_repair : parentDefault.for_refurbish) : false;
  };

  const isInherited = (itemId: string, type: "repair" | "refurbish") => {
    if (isAllBrands) return false;
    const brandDefault = defaults.find(d => d.workflow_item_id === itemId);
    if (brandDefault) return false; // Has brand-specific override
    const parentDefault = parentDefaults.find(d => d.workflow_item_id === itemId);
    return parentDefault ? (type === "repair" ? parentDefault.for_repair : parentDefault.for_refurbish) : false;
  };

  const toggleDefault = async (itemId: string, type: "repair" | "refurbish") => {
    if (isAllBrands) {
      // Toggle parent (brand=null) default
      const existing = parentDefaults.find(d => d.workflow_item_id === itemId);
      if (existing) {
        const newVal = type === "repair" ? !existing.for_repair : !existing.for_refurbish;
        const updates = type === "repair" ? { for_repair: newVal } : { for_refurbish: newVal };
        const otherVal = type === "repair" ? existing.for_refurbish : existing.for_repair;
        if (!newVal && !otherVal) {
          await supabase.from("workflow_defaults").delete().eq("id", existing.id);
          setParentDefaults(prev => prev.filter(d => d.id !== existing.id));
        } else {
          await supabase.from("workflow_defaults").update(updates).eq("id", existing.id);
          setParentDefaults(prev => prev.map(d => d.id === existing.id ? { ...d, ...updates } : d));
        }
      } else {
        const newDefault = {
          appliance_type: applianceType,
          brand: null as string | null,
          workflow_item_id: itemId,
          for_repair: type === "repair",
          for_refurbish: type === "refurbish",
        };
        const { data } = await supabase.from("workflow_defaults").insert(newDefault).select().single();
        if (data) setParentDefaults(prev => [...prev, data as WorkflowDefault]);
      }
      return;
    }

    if (!selectedBrand) {
      toast({ title: "Select a brand first", variant: "destructive" });
      return;
    }
    const existing = defaults.find(d => d.workflow_item_id === itemId);
    if (existing) {
      const newVal = type === "repair" ? !existing.for_repair : !existing.for_refurbish;
      const updates = type === "repair" ? { for_repair: newVal } : { for_refurbish: newVal };
      const otherVal = type === "repair" ? existing.for_refurbish : existing.for_repair;
      if (!newVal && !otherVal) {
        await supabase.from("workflow_defaults").delete().eq("id", existing.id);
        setDefaults(prev => prev.filter(d => d.id !== existing.id));
      } else {
        await supabase.from("workflow_defaults").update(updates).eq("id", existing.id);
        setDefaults(prev => prev.map(d => d.id === existing.id ? { ...d, ...updates } : d));
      }
    } else {
      // Create brand-specific override - start from parent state and toggle
      const parentDefault = parentDefaults.find(d => d.workflow_item_id === itemId);
      const parentRepair = parentDefault?.for_repair || false;
      const parentRefurbish = parentDefault?.for_refurbish || false;
      const newDefault = {
        appliance_type: applianceType,
        brand: selectedBrand,
        workflow_item_id: itemId,
        for_repair: type === "repair" ? !parentRepair : parentRepair,
        for_refurbish: type === "refurbish" ? !parentRefurbish : parentRefurbish,
      };
      const { data } = await supabase.from("workflow_defaults").insert(newDefault).select().single();
      if (data) setDefaults(prev => [...prev, data as WorkflowDefault]);
    }
  };

  // ---- Estimated minutes helpers ----
  const getMinutes = (itemId: string): number | null => {
    const item = items.find(i => i.id === itemId);
    // Error codes are brand-scoped items themselves — read minutes directly from the item
    if (item?.category === "error_codes") {
      return item?.estimated_minutes ?? null;
    }
    if (isAllBrands) {
      return item?.estimated_minutes ?? null;
    }
    const brandDefault = defaults.find(d => d.workflow_item_id === itemId);
    if (brandDefault && brandDefault.estimated_minutes != null) {
      return brandDefault.estimated_minutes;
    }
    return item?.estimated_minutes ?? null;
  };

  const isMinutesInherited = (itemId: string): boolean => {
    if (isAllBrands) return false;
    const item = items.find(i => i.id === itemId);
    // Error codes are brand-specific by nature — never inherited
    if (item?.category === "error_codes") return false;
    const brandDefault = defaults.find(d => d.workflow_item_id === itemId);
    return !brandDefault || brandDefault.estimated_minutes == null;
  };

  const setMinutes = async (itemId: string, minutes: number | null) => {
    const item = items.find(i => i.id === itemId);
    // Error codes: store minutes directly on the workflow_items row (brand-scoped item)
    const isErrorCode = item?.category === "error_codes";

    if (isAllBrands || isErrorCode) {
      const { error } = await supabase
        .from("workflow_items")
        .update({ estimated_minutes: minutes })
        .eq("id", itemId);
      if (error) {
        toast({ title: "Failed to save time", description: error.message, variant: "destructive" });
        return;
      }
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, estimated_minutes: minutes } : i));
      return;
    }

    if (!selectedBrand) {
      toast({ title: "Select a brand first", variant: "destructive" });
      return;
    }

    const existing = defaults.find(d => d.workflow_item_id === itemId);
    if (existing) {
      const { error } = await supabase
        .from("workflow_defaults")
        .update({ estimated_minutes: minutes })
        .eq("id", existing.id);
      if (error) {
        toast({ title: "Failed to save time", description: error.message, variant: "destructive" });
        return;
      }
      setDefaults(prev => prev.map(d => d.id === existing.id ? { ...d, estimated_minutes: minutes } : d));
    } else {
      // Create a brand override row carrying inherited repair/refurb flags
      const parentDefault = parentDefaults.find(d => d.workflow_item_id === itemId);
      const newRow = {
        appliance_type: applianceType,
        brand: selectedBrand,
        workflow_item_id: itemId,
        for_repair: parentDefault?.for_repair ?? false,
        for_refurbish: parentDefault?.for_refurbish ?? false,
        estimated_minutes: minutes,
      };
      const { data, error } = await supabase.from("workflow_defaults").insert(newRow).select().single();
      if (error) {
        toast({ title: "Failed to save time", description: error.message, variant: "destructive" });
        return;
      }
      if (data) setDefaults(prev => [...prev, data as WorkflowDefault]);
    }
  };

  const isPhaseChecked = (phaseCategory: string, type: "repair" | "refurbish"): boolean => {
    if (isAllBrands) {
      const d = parentPhaseDefaults.find(d => d.phase_category === phaseCategory);
      // Default to enabled when no row exists.
      if (!d) return true;
      return type === "repair" ? d.for_repair : d.for_refurbish;
    }
    const brandRow = phaseDefaults.find(d => d.phase_category === phaseCategory);
    if (brandRow) return type === "repair" ? brandRow.for_repair : brandRow.for_refurbish;
    const parentRow = parentPhaseDefaults.find(d => d.phase_category === phaseCategory);
    if (!parentRow) return true;
    return type === "repair" ? parentRow.for_repair : parentRow.for_refurbish;
  };

  const isPhaseInherited = (phaseCategory: string): boolean => {
    if (isAllBrands) return false;
    return !phaseDefaults.find(d => d.phase_category === phaseCategory);
  };

  const togglePhaseDefault = async (phaseCategory: string, type: "repair" | "refurbish") => {
    if (isAllBrands) {
      const existing = parentPhaseDefaults.find(d => d.phase_category === phaseCategory);
      if (existing) {
        const updates = type === "repair"
          ? { for_repair: !existing.for_repair }
          : { for_refurbish: !existing.for_refurbish };
        const { error } = await supabase.from("workflow_phase_defaults").update(updates).eq("id", existing.id);
        if (error) {
          toast({ title: "Failed to update phase", description: error.message, variant: "destructive" });
          return;
        }
        setParentPhaseDefaults(prev => prev.map(d => d.id === existing.id ? { ...d, ...updates } : d));
      } else {
        const newRow = {
          appliance_type: applianceType,
          phase_category: phaseCategory,
          brand: null as string | null,
          for_repair: type === "repair" ? false : true,
          for_refurbish: type === "refurbish" ? false : true,
        };
        const { data, error } = await supabase.from("workflow_phase_defaults").insert(newRow).select().single();
        if (error) {
          toast({ title: "Failed to update phase", description: error.message, variant: "destructive" });
          return;
        }
        if (data) setParentPhaseDefaults(prev => [...prev, data as PhaseDefault]);
      }
      return;
    }

    // Brand-specific
    if (!selectedBrand) return;
    const existing = phaseDefaults.find(d => d.phase_category === phaseCategory);
    if (existing) {
      const updates = type === "repair"
        ? { for_repair: !existing.for_repair }
        : { for_refurbish: !existing.for_refurbish };
      const { error } = await supabase.from("workflow_phase_defaults").update(updates).eq("id", existing.id);
      if (error) {
        toast({ title: "Failed to update phase", description: error.message, variant: "destructive" });
        return;
      }
      setPhaseDefaults(prev => prev.map(d => d.id === existing.id ? { ...d, ...updates } : d));
    } else {
      // Start from inherited (parent or default true) values, then flip the toggled flag.
      const parentRepair = isPhaseChecked(phaseCategory, "repair");
      const parentRefurbish = isPhaseChecked(phaseCategory, "refurbish");
      const newRow = {
        appliance_type: applianceType,
        phase_category: phaseCategory,
        brand: selectedBrand,
        for_repair: type === "repair" ? !parentRepair : parentRepair,
        for_refurbish: type === "refurbish" ? !parentRefurbish : parentRefurbish,
      };
      const { data, error } = await supabase.from("workflow_phase_defaults").insert(newRow).select().single();
      if (error) {
        toast({ title: "Failed to update phase", description: error.message, variant: "destructive" });
        return;
      }
      if (data) setPhaseDefaults(prev => [...prev, data as PhaseDefault]);
    }
  };

  const getPhaseMinutes = (phaseCategory: string): number | null => {
    if (isAllBrands) {
      const d = parentPhaseDefaults.find(d => d.phase_category === phaseCategory);
      return d?.estimated_minutes ?? null;
    }
    const brandRow = phaseDefaults.find(d => d.phase_category === phaseCategory);
    if (brandRow && brandRow.estimated_minutes != null) return Number(brandRow.estimated_minutes);
    const parentRow = parentPhaseDefaults.find(d => d.phase_category === phaseCategory);
    return parentRow?.estimated_minutes != null ? Number(parentRow.estimated_minutes) : null;
  };

  const isPhaseMinutesInherited = (phaseCategory: string): boolean => {
    if (isAllBrands) return false;
    const brandRow = phaseDefaults.find(d => d.phase_category === phaseCategory);
    return !brandRow || brandRow.estimated_minutes == null;
  };

  const setPhaseMinutes = async (phaseCategory: string, minutes: number | null) => {
    if (isAllBrands) {
      const existing = parentPhaseDefaults.find(d => d.phase_category === phaseCategory);
      if (existing) {
        const { error } = await supabase
          .from("workflow_phase_defaults")
          .update({ estimated_minutes: minutes })
          .eq("id", existing.id);
        if (error) {
          toast({ title: "Failed to save phase time", description: error.message, variant: "destructive" });
          return;
        }
        setParentPhaseDefaults(prev => prev.map(d => d.id === existing.id ? { ...d, estimated_minutes: minutes } : d));
      } else {
        const newRow = {
          appliance_type: applianceType,
          phase_category: phaseCategory,
          brand: null as string | null,
          for_repair: true,
          for_refurbish: true,
          estimated_minutes: minutes,
        };
        const { data, error } = await supabase.from("workflow_phase_defaults").insert(newRow).select().single();
        if (error) {
          toast({ title: "Failed to save phase time", description: error.message, variant: "destructive" });
          return;
        }
        if (data) setParentPhaseDefaults(prev => [...prev, data as PhaseDefault]);
      }
      return;
    }

    if (!selectedBrand) return;
    const existing = phaseDefaults.find(d => d.phase_category === phaseCategory);
    if (existing) {
      const { error } = await supabase
        .from("workflow_phase_defaults")
        .update({ estimated_minutes: minutes })
        .eq("id", existing.id);
      if (error) {
        toast({ title: "Failed to save phase time", description: error.message, variant: "destructive" });
        return;
      }
      setPhaseDefaults(prev => prev.map(d => d.id === existing.id ? { ...d, estimated_minutes: minutes } : d));
    } else {
      const parentRow = parentPhaseDefaults.find(d => d.phase_category === phaseCategory);
      const newRow = {
        appliance_type: applianceType,
        phase_category: phaseCategory,
        brand: selectedBrand,
        for_repair: parentRow?.for_repair ?? true,
        for_refurbish: parentRow?.for_refurbish ?? true,
        estimated_minutes: minutes,
      };
      const { data, error } = await supabase.from("workflow_phase_defaults").insert(newRow).select().single();
      if (error) {
        toast({ title: "Failed to save phase time", description: error.message, variant: "destructive" });
        return;
      }
      if (data) setPhaseDefaults(prev => [...prev, data as PhaseDefault]);
    }
  };

  const addItem = async (category: string) => {
    if (!newTitle.trim()) return;
    setSaving(true);
    const categoryItems = items.filter(i => i.category === category && (category === "error_codes" ? i.brand === selectedBrand : !i.brand));
    const maxSort = Math.max(0, ...categoryItems.map(i => i.sort_order));
    const newItem = {
      category,
      appliance_type: applianceType,
      brand: category === "error_codes" ? selectedBrand : null,
      title: newTitle.trim(),
      description: newDescription.trim() || null,
      sort_order: maxSort + 1,
    };
    const { data, error } = await supabase.from("workflow_items").insert([newItem as any]).select().single();
    if (error) {
      toast({ title: "Error adding item", description: error.message, variant: "destructive" });
    } else if (data) {
      setItems(prev => [...prev, data as WorkflowItem]);
      setNewTitle("");
      setNewDescription("");
      setNewItemCategory(null);
      toast({ title: "Item added" });
    }
    setSaving(false);
  };

  const deleteItem = async (id: string) => {
    const item = items.find(i => i.id === id);
    if (item?.is_mandatory && !isAllBrands) {
      toast({ title: "Cannot delete mandatory item", description: "This step is mandatory and can only be removed from the 'All Brands' view.", variant: "destructive" });
      return;
    }
    await supabase.from("workflow_items").delete().eq("id", id);
    setItems(prev => prev.filter(i => i.id !== id));
    toast({ title: "Item removed" });
  };

  const toggleMandatory = async (id: string) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    const newVal = !item.is_mandatory;
    await supabase.from("workflow_items").update({ is_mandatory: newVal }).eq("id", id);
    setItems(prev => prev.map(i => i.id === id ? { ...i, is_mandatory: newVal } : i));
    toast({ title: newVal ? "Step marked as mandatory" : "Step no longer mandatory" });
  };

  const getItemsForCategory = (category: string) => {
    if (category === "error_codes") {
      if (isAllBrands) {
        // Show all error codes across brands when no specific brand is selected
        return items.filter(i => i.category === category);
      }
      return items.filter(i => i.category === category && i.brand === selectedBrand);
    }
    if (category === "tips") {
      if (isAllBrands) {
        return items.filter(i => i.category === category);
      }
      return items.filter(i => i.category === category && (!i.brand || i.brand === selectedBrand));
    }
    return items.filter(i => i.category === category && !i.brand);
  };

  const findCategoryForItem = (itemId: string): string | null => {
    const item = items.find(i => i.id === itemId);
    if (!item) return null;
    // Determine which visual category this item belongs to
    for (const cat of CATEGORY_CONFIG) {
      const catItems = getItemsForCategory(cat.key);
      if (catItems.some(i => i.id === itemId)) return cat.key;
    }
    return item.category;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeItem = items.find(i => i.id === active.id);
    if (!activeItem) return;

    // Determine target category: either from droppable area or from the item being dropped on
    let targetCategory: string;
    const overItem = items.find(i => i.id === over.id);

    if (overItem) {
      // Dropped on another item - use that item's visual category
      targetCategory = findCategoryForItem(over.id as string) || overItem.category;
    } else {
      // Dropped on a droppable category zone
      targetCategory = over.id as string;
    }

    const sourceCategory = findCategoryForItem(active.id as string) || activeItem.category;
    const categoryChanged = sourceCategory !== targetCategory;

    // Build the new ordered list for the target category
    const targetItems = getItemsForCategory(targetCategory).filter(i => i.id !== active.id);

    // Determine brand for the moved item
    let newBrand = activeItem.brand;
    if (categoryChanged) {
      if (targetCategory === "error_codes") {
        newBrand = selectedBrand || null;
      } else if (targetCategory === "tips") {
        // Keep existing brand or null
      } else {
        newBrand = null;
      }
    }

    // Find insertion index
    let insertIndex = targetItems.length;
    if (overItem) {
      const overIndex = targetItems.findIndex(i => i.id === over.id);
      if (overIndex >= 0) insertIndex = overIndex;
    }

    // Insert active item at position
    const movedItem: WorkflowItem = {
      ...activeItem,
      category: targetCategory,
      brand: newBrand,
      sort_order: 0,
    };
    const newOrder = [...targetItems];
    newOrder.splice(insertIndex, 0, movedItem);

    // Update sort_order for all items in target category
    const updates: PromiseLike<any>[] = [];
    const updatedItems = newOrder.map((item, idx) => {
      const updated = { ...item, sort_order: idx + 1 };
      if (item.id === active.id) {
        // Update category, brand, and sort_order
        updates.push(
          supabase.from("workflow_items").update({
            category: targetCategory as any,
            brand: newBrand,
            sort_order: idx + 1,
          }).eq("id", item.id)
        );
      } else if (item.sort_order !== idx + 1) {
        updates.push(
          supabase.from("workflow_items").update({ sort_order: idx + 1 }).eq("id", item.id)
        );
      }
      return updated;
    });

    // If category changed, also re-sort the source category
    if (categoryChanged) {
      const sourceItems = getItemsForCategory(sourceCategory).filter(i => i.id !== active.id);
      sourceItems.forEach((item, idx) => {
        if (item.sort_order !== idx + 1) {
          updates.push(
            supabase.from("workflow_items").update({ sort_order: idx + 1 }).eq("id", item.id)
          );
        }
      });
    }

    // Optimistically update local state
    setItems(prev => {
      const rest = prev.filter(i => i.id !== active.id);
      const updated = rest.map(i => {
        const inNewOrder = updatedItems.find(u => u.id === i.id);
        return inNewOrder || i;
      });
      return [...updated, movedItem].map(i => {
        const inNewOrder = updatedItems.find(u => u.id === i.id);
        return inNewOrder || i;
      });
    });

    // Persist
    await Promise.all(updates);

    if (categoryChanged) {
      toast({ title: `Moved to ${CATEGORY_CONFIG.find(c => c.key === targetCategory)?.label}` });
    }
  };

  const activeItem = activeId ? items.find(i => i.id === activeId) : null;

  if (roleLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }
  // Access enforced by the route guard in App.tsx.

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-2xl font-bold text-foreground mb-1">{t("workflows.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("workflows.subtitle")}</p>
      </div>

      <div className="px-6">
        <Tabs value={applianceType} onValueChange={setApplianceType}>
          <TabsList className="w-full">
            {APPLIANCE_TYPES.map(t => (
              <TabsTrigger key={t} value={t} className="flex-1 text-xs">{t}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="px-6 mt-4">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-foreground">Brand:</label>
          <SearchableSelect
            className="w-48"
            value={selectedBrand}
            onChange={(v) => setSelectedBrand(v ?? "__all__")}
            placeholder="Select brand"
            searchPlaceholder="Search brands…"
            emptyMessage="No brands found"
            items={[
              { value: "__all__", label: "All Brands (default)" },
              ...brands.map((b) => ({ value: b, label: b })),
            ]}
          />
          <span className="text-xs text-muted-foreground">
            {isAllBrands ? "(parent defaults inherited by all brands)" : "(brand overrides — inherits from parent)"}
          </span>
        </div>
      </div>

      {/* Triage Access Control */}
      <div className="px-6 mt-4">
        {(() => {
          const triageRoles = getRolesForPhase("triage");
          const COMPANY_ROLES = ["admin"];
          return (
            <div className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <ClipboardList className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground text-sm">Triage Assessment</p>
                  <p className="text-xs text-muted-foreground">Pre-refurbishment condition assessment</p>
                </div>
                {triageRoles.length > 0 && (
                  <div className="flex items-center gap-1 mr-2">
                    <Shield className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-xs text-amber-500 font-medium">{triageRoles.length}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                      <Shield className="w-3 h-3" /> Manage Access
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3" align="start">
                    <p className="text-sm font-medium text-foreground mb-2">Triage Access Roles</p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Assign roles that can perform the triage assessment. If none assigned, everyone has access.
                    </p>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Company Roles</p>
                      {COMPANY_ROLES.map((role) => {
                        const isAssigned = triageRoles.some(r => r.role_type === "company" && r.role_value === role);
                        return (
                          <label key={role} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/50 cursor-pointer">
                            <Checkbox
                              checked={isAssigned}
                              onCheckedChange={() => {
                                const existing = triageRoles.find(r => r.role_type === "company" && r.role_value === role);
                                if (existing) {
                                  removeRoleFromPhase(existing.id);
                                } else {
                                  addRoleToPhase("triage", "company", role);
                                }
                              }}
                            />
                            <span className="text-sm">Company admin</span>
                          </label>
                        );
                      })}
                      {customRoles.length > 0 && (
                        <>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mt-3">Custom Roles</p>
                          {customRoles.map((role) => {
                            const isAssigned = triageRoles.some(r => r.role_type === "custom" && r.role_value === role.id);
                            return (
                              <label key={role.id} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/50 cursor-pointer">
                                <Checkbox
                                  checked={isAssigned}
                                  onCheckedChange={() => {
                                    const existing = triageRoles.find(r => r.role_type === "custom" && r.role_value === role.id);
                                    if (existing) {
                                      removeRoleFromPhase(existing.id);
                                    } else {
                                      addRoleToPhase("triage", "custom", role.id);
                                    }
                                  }}
                                />
                                <span className="text-sm">{role.name}</span>
                              </label>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                {triageRoles.map((ar) => {
                  const displayName = ar.role_type === "company"
                    ? ar.role_value
                    : customRoles.find(c => c.id === ar.role_value)?.name || ar.role_value;
                  return (
                    <Badge key={ar.id} variant="secondary" className="text-xs gap-1">
                      {displayName}
                      <button onClick={() => removeRoleFromPhase(ar.id)} className="ml-0.5 hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  );
                })}
                {triageRoles.length === 0 && (
                  <span className="text-xs text-muted-foreground italic">All roles can access triage</span>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="px-6 mt-4 space-y-3">
            {CATEGORY_CONFIG.map(({ key, label, icon: Icon, brandSpecific }) => {
              const categoryItems = getItemsForCategory(key);
              const isOpen = openSections[key];
              const assignedRoles = getRolesForPhase(key);
              const COMPANY_ROLES = ["admin"];

              const handleToggleRole = async (roleType: "company" | "custom", roleValue: string, e: React.MouseEvent) => {
                e.stopPropagation();
                const existing = assignedRoles.find(r => r.role_type === roleType && r.role_value === roleValue);
                if (existing) {
                  await removeRoleFromPhase(existing.id);
                  toast({ title: "Role removed from phase" });
                } else {
                  const { error } = await addRoleToPhase(key, roleType, roleValue);
                  if (error) {
                    toast({ title: "Error assigning role", description: error.message, variant: "destructive" });
                  } else {
                    toast({ title: "Role assigned to phase" });
                  }
                }
              };

              return (
                <Collapsible key={key} open={isOpen} onOpenChange={(o) => setOpenSections(prev => ({ ...prev, [key]: o }))}>
                  <div className="w-full bg-card rounded-xl border border-border">
                    <CollapsibleTrigger className="w-full p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-foreground text-sm">{label}</p>
                        <p className="text-xs text-muted-foreground">
                          {brandSpecific === true ? `Brand-specific • ${categoryItems.length} items` : key === "tips" ? `Generic + brand-specific • ${categoryItems.length} items` : `Brand-agnostic • ${categoryItems.length} items`}
                        </p>
                      </div>
                      {assignedRoles.length > 0 && (
                        <div className="flex items-center gap-1 mr-2">
                          <Shield className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-xs text-amber-500 font-medium">{assignedRoles.length}</span>
                        </div>
                      )}
                      {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </CollapsibleTrigger>

                    {/* Role assignment bar */}
                    {isOpen && (
                      <div className="px-4 pb-3 flex items-center gap-2 flex-wrap border-b border-border">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                              <Shield className="w-3 h-3" /> Manage Access
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3" align="start" onClick={(e) => e.stopPropagation()}>
                            <p className="text-sm font-medium text-foreground mb-2">Phase Access Roles</p>
                            <p className="text-xs text-muted-foreground mb-3">
                              Assign roles that can work on this phase. If none assigned, everyone has access.
                            </p>
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Company Roles</p>
                              {COMPANY_ROLES.map((role) => {
                                const isAssigned = assignedRoles.some(r => r.role_type === "company" && r.role_value === role);
                                return (
                                  <label key={role} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/50 cursor-pointer">
                                    <Checkbox
                                      checked={isAssigned}
                                      onCheckedChange={() => {
                                        const existing = assignedRoles.find(r => r.role_type === "company" && r.role_value === role);
                                        if (existing) {
                                          removeRoleFromPhase(existing.id);
                                        } else {
                                          addRoleToPhase(key, "company", role);
                                        }
                                      }}
                                    />
                                    <span className="text-sm">Company admin</span>
                                  </label>
                                );
                              })}
                              {customRoles.length > 0 && (
                                <>
                                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mt-3">Custom Roles</p>
                                  {customRoles.map((role) => {
                                    const isAssigned = assignedRoles.some(r => r.role_type === "custom" && r.role_value === role.id);
                                    return (
                                      <label key={role.id} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/50 cursor-pointer">
                                        <Checkbox
                                          checked={isAssigned}
                                          onCheckedChange={() => {
                                            const existing = assignedRoles.find(r => r.role_type === "custom" && r.role_value === role.id);
                                            if (existing) {
                                              removeRoleFromPhase(existing.id);
                                            } else {
                                              addRoleToPhase(key, "custom", role.id);
                                            }
                                          }}
                                        />
                                        <span className="text-sm">{role.name}</span>
                                      </label>
                                    );
                                  })}
                                </>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {assignedRoles.map((ar) => {
                          const displayName = ar.role_type === "company"
                            ? ar.role_value
                            : customRoles.find(c => c.id === ar.role_value)?.name || ar.role_value;
                          return (
                            <Badge key={ar.id} variant="secondary" className="text-xs gap-1">
                              {displayName}
                              <button
                                onClick={(e) => { e.stopPropagation(); removeRoleFromPhase(ar.id); }}
                                className="ml-0.5 hover:text-destructive"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          );
                        })}
                        {assignedRoles.length === 0 && (
                          <span className="text-xs text-muted-foreground italic">All roles can access this phase</span>
                        )}
                      </div>
                    )}
                    {/* Phase enable/disable for repair vs refurbish */}
                    {isOpen && (
                      <div className="px-4 py-3 flex items-center gap-4 flex-wrap border-b border-border bg-muted/10">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Use phase in
                        </span>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={isPhaseChecked(key, "repair")}
                            onCheckedChange={() => togglePhaseDefault(key, "repair")}
                          />
                          <span className={isPhaseInherited(key) ? "text-muted-foreground" : "text-foreground"}>
                            Repair
                          </span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={isPhaseChecked(key, "refurbish")}
                            onCheckedChange={() => togglePhaseDefault(key, "refurbish")}
                          />
                          <span className={isPhaseInherited(key) ? "text-muted-foreground" : "text-foreground"}>
                            Refurbish
                          </span>
                        </label>
                        {!isAllBrands && isPhaseInherited(key) && (
                          <span className="text-xs text-muted-foreground italic">inherited from All Brands</span>
                        )}
                        {key === "error_codes" && (
                          <PhaseMinutesInput
                            value={getPhaseMinutes(key)}
                            inherited={isPhaseMinutesInherited(key)}
                            onChange={(m) => setPhaseMinutes(key, m)}
                            label="Error Code Diagnostic time"
                          />
                        )}
                        <span className="text-xs text-muted-foreground ml-auto">
                          Disabling hides the entire phase from that workflow.
                        </span>
                      </div>
                    )}
                  </div>
                  <CollapsibleContent className="mt-1">
                    <DroppableCategory id={key}>
                      <div className="bg-card rounded-xl border border-border overflow-hidden">
                        {categoryItems.length > 0 && (
                          <div className="grid grid-cols-[28px_1fr_60px_60px_72px_28px_36px_36px] gap-2 px-4 py-2 border-b border-border bg-muted/30 text-xs font-medium text-muted-foreground">
                            <span></span>
                            <span>Item</span>
                            <span className="text-center">Repair</span>
                            <span className="text-center">Refurb</span>
                            <span className="text-center" title="Estimated minutes per step">Min</span>
                            <span className="text-center">{isAllBrands ? "Lock" : ""}</span>
                            <span className="text-center">{key === "error_codes" ? <Link2 className="w-3 h-3 mx-auto" /> : ""}</span>
                            <span></span>
                          </div>
                        )}
                        <SortableContext items={categoryItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                          {categoryItems.map(item => (
                            <DraggableWorkflowItem
                              key={item.id}
                              item={item}
                              categoryKey={key}
                              isRepairChecked={isChecked(item.id, "repair")}
                              isRefurbishChecked={isChecked(item.id, "refurbish")}
                              isRepairInherited={isInherited(item.id, "repair")}
                              isRefurbishInherited={isInherited(item.id, "refurbish")}
                              selectedBrand={selectedBrand}
                              isAllBrands={isAllBrands}
                              onToggleDefault={toggleDefault}
                              onDelete={deleteItem}
                              onToggleMandatory={toggleMandatory}
                              mappingCount={key === "error_codes" ? mappings.filter(m => m.source_item_id === item.id).length : 0}
                              onOpenMappings={key === "error_codes" ? (item) => setMappingDialogItem(item) : undefined}
                              estimatedMinutes={getMinutes(item.id)}
                              isMinutesInherited={isMinutesInherited(item.id)}
                              onChangeMinutes={setMinutes}
                            />
                          ))}
                        </SortableContext>
                        {categoryItems.length === 0 && (
                          <p className="px-4 py-4 text-sm text-muted-foreground italic">
                            No items yet{brandSpecific && selectedBrand ? ` for ${selectedBrand}` : ""}. Drag items here or add new ones.
                          </p>
                        )}
                        {newItemCategory === key ? (
                          <div className="px-4 py-3 border-t border-border space-y-2 bg-muted/20">
                            <Input placeholder="Title" value={newTitle} onChange={e => setNewTitle(e.target.value)} className="text-sm" />
                            <Textarea placeholder="Description (optional)" value={newDescription} onChange={e => setNewDescription(e.target.value)} className="text-sm min-h-[60px]" />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => addItem(key)} disabled={saving || !newTitle.trim()}>
                                <Save className="w-3.5 h-3.5 mr-1" /> Add
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setNewItemCategory(null); setNewTitle(""); setNewDescription(""); }}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setNewItemCategory(key)}
                            className="w-full px-4 py-3 border-t border-border text-sm text-primary flex items-center gap-1 hover:bg-muted/30 transition-colors"
                          >
                            <Plus className="w-3.5 h-3.5" /> Add item
                          </button>
                        )}
                      </div>
                    </DroppableCategory>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>

          <DragOverlay>
            {activeItem ? (
              <div className="bg-card border border-primary/30 rounded-lg shadow-xl px-4 py-3 text-sm font-medium text-foreground opacity-90">
                {activeItem.title}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {mappingDialogItem && (
        <ItemMappingDialog
          open={!!mappingDialogItem}
          onOpenChange={(open) => { if (!open) setMappingDialogItem(null); }}
          sourceItem={mappingDialogItem}
          allItems={items}
          mappings={mappings}
          onMappingsChange={async () => {
            const errorCodeIds = items.filter(i => i.category === "error_codes").map(i => i.id);
            if (errorCodeIds.length > 0) {
              const { data } = await supabase
                .from("workflow_item_mappings")
                .select("id, source_item_id, target_item_id")
                .in("source_item_id", errorCodeIds);
              setMappings((data || []) as { id: string; source_item_id: string; target_item_id: string }[]);
            }
          }}
        />
      )}
    </div>
  );
}
